import { useEffect, useMemo, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { supabase } from "../lib/supabaseClient"
import { Lock, Mail, User, Loader2 } from "lucide-react"
import { useLang } from "../context/LangContext"

type OAuthProvider = "google" | "facebook" | "azure"

const T = {
  en: {
    title: "Create your account",
    sub: "Join uCloud360 and start building with clarity",
    google: "Continue with Google",
    microsoft: "Continue with Microsoft",
    facebook: "Continue with Facebook",
    or: "or",
    nameLabel: "Full name",
    namePH: "Your name",
    emailLabel: "Email",
    emailPH: "you@company.com",
    passLabel: "Password",
    passPH: "Minimum 8 characters",
    createBtn: "Create account",
    hasAccount: "Already have an account?",
    signIn: "Sign in",
    legalNote: "By creating an account, you agree to our",
    terms: "Terms",
    and: "and",
    privacy: "Privacy Policy",
    successMsg: "Account created! Check your email to confirm.",
  },
  es: {
    title: "Crea tu cuenta",
    sub: "Únete a uCloud360 y empieza a construir con claridad",
    google: "Continuar con Google",
    microsoft: "Continuar con Microsoft",
    facebook: "Continuar con Facebook",
    or: "o",
    nameLabel: "Nombre completo",
    namePH: "Tu nombre",
    emailLabel: "Correo electrónico",
    emailPH: "tu@empresa.com",
    passLabel: "Contraseña",
    passPH: "Mínimo 8 caracteres",
    createBtn: "Crear cuenta",
    hasAccount: "¿Ya tienes cuenta?",
    signIn: "Iniciar sesión",
    legalNote: "Al crear una cuenta, aceptas nuestros",
    terms: "Términos",
    and: "y",
    privacy: "Política de Privacidad",
    successMsg: "¡Cuenta creada! Revisa tu correo para confirmar.",
  },
}

export default function RegisterPage() {
  const navigate = useNavigate()
  const { lang } = useLang()
  const t = T[lang]

  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const redirectTo = useMemo(() => `${window.location.origin}/app/dashboard`, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate("/app/dashboard")
    })
  }, [navigate])

  const signInOAuth = async (provider: OAuthProvider) => {
    setLoading(true); setError(null)
    try {
      const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo } })
      if (error) throw error
    } catch (e: any) {
      setError(e?.message ?? "OAuth error")
      setLoading(false)
    }
  }

  const signUpEmailPassword = async () => {
    if (password.length < 8) { setError("Password must be at least 8 characters."); return }
    setLoading(true); setError(null); setMessage(null)
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectTo,
          data: { full_name: fullName },
        },
      })
      if (error) throw error
      setMessage(t.successMsg)
    } catch (e: any) {
      setError(e?.message ?? "Sign up failed.")
    } finally {
      setLoading(false)
    }
  }

  const oauthBtn = "w-full rounded-xl border border-gray-200 dark:border-gray-800 px-4 py-3 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-900 transition disabled:opacity-60 text-gray-800 dark:text-gray-200"

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-950 p-8 shadow-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <img src="/logo.png" alt="uCloud360" className="h-8 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold tracking-tight">{t.title}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t.sub}</p>
          </div>

          {/* OAuth */}
          <div className="grid gap-3">
            <button type="button" onClick={() => signInOAuth("google")} disabled={loading} className={oauthBtn}>{t.google}</button>
            <button type="button" onClick={() => signInOAuth("azure")} disabled={loading} className={oauthBtn}>{t.microsoft}</button>
            <button type="button" onClick={() => signInOAuth("facebook")} disabled={loading} className={oauthBtn}>{t.facebook}</button>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
            <span className="text-xs text-gray-400">{t.or}</span>
            <div className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
          </div>

          {/* Form */}
          <form onSubmit={(e) => { e.preventDefault(); signUpEmailPassword() }} className="grid gap-4">
            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">{t.nameLabel}</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-white dark:bg-black">
                <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t.namePH} className="w-full bg-transparent outline-none text-sm text-black dark:text-white placeholder-gray-400" autoComplete="name" />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">{t.emailLabel}</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-white dark:bg-black">
                <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.emailPH} className="w-full bg-transparent outline-none text-sm text-black dark:text-white placeholder-gray-400" autoComplete="email" />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 dark:text-gray-400">{t.passLabel}</label>
              <div className="mt-1 flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-800 px-3 py-2.5 bg-white dark:bg-black">
                <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t.passPH} className="w-full bg-transparent outline-none text-sm text-black dark:text-white placeholder-gray-400" autoComplete="new-password" />
              </div>
            </div>

            {error && <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-700 dark:text-red-300">{error}</div>}
            {message && <div className="rounded-xl border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/30 p-3 text-sm text-green-700 dark:text-green-300">{message}</div>}

            <button type="submit" disabled={loading} className="w-full rounded-xl bg-[#2563EB] text-white hover:bg-[#1d4ed8] px-4 py-3 text-sm font-semibold transition disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t.createBtn}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {t.hasAccount}{" "}
            <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:underline">{t.signIn}</Link>
          </p>
        </div>

        <p className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
          {t.legalNote}{" "}
          <Link to="/terms" className="underline hover:text-gray-600 dark:hover:text-gray-300">{t.terms}</Link>
          {" "}{t.and}{" "}
          <Link to="/privacy" className="underline hover:text-gray-600 dark:hover:text-gray-300">{t.privacy}</Link>.
        </p>
      </div>
    </div>
  )
}
